/**
 * La CARTA D'IDENTITÀ del componente canonico — il pannello di cura dell'anagrafe.
 *
 * Qui si tocca SOLO ciò che è universale: chi lo costruisce, come si chiama, le sue
 * specifiche e i suoi media (logo, texture, modello 3D, schede). Il prezzo non compare
 * per scelta: è dei tenant, e questa pagina non deve nemmeno fargli ombra.
 *
 * I media salvano da soli al caricamento (un upload è già un'azione deliberata);
 * identità e specifiche invece si confermano col bottone, perché si scrivono a mano
 * e una battitura a metà non deve finire nel database.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import UploadFileOutlined from '@mui/icons-material/UploadFileOutlined';
import ViewInAr from '@mui/icons-material/ViewInAr';
import { SoftChip } from '../SoftChip';
import type { ComponenteCanonico, Marchio, MediaAnagrafe, MediaTipoAnagrafe } from '../../types/anagrafe';
import { caricaMediaSuStorage, mediaAggiornati, salvaComponente } from '../../services/anagrafeService';
import { RiconoscimentoMagazzino, aPatch, valoriDaComponente, type RiconoscimentoValori } from './RiconoscimentoMagazzino';

interface Props {
  componente: ComponenteCanonico | null;
  /** Il registro dei marchi, per la tendina e per allineare `costruttore` al salvataggio. */
  marchi: Marchio[];
  onChiudi: () => void;
  /** Il documento è cambiato: la pagina aggiorna la sua copia in lista. */
  onAggiornato: (aggiornato: ComponenteCanonico) => void;
  onEsito: (severity: 'success' | 'error', message: string) => void;
}

/** Il valore di una specifica, dal testo: numero se lo è davvero, testo altrimenti. */
function valoreDaTesto(t: string): unknown {
  const trimmed = t.trim();
  if (trimmed === '') return '';
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && /^[\d.,\-+eE]+$/.test(trimmed) ? n : trimmed;
}

const testoDaValore = (v: unknown): string => (typeof v === 'object' ? JSON.stringify(v) : String(v ?? ''));

/** Bersagli da 40 px: la console si usa anche da tablet. */
const ALTEZZA_TOCCO = 40;

export const SchedaComponente: React.FC<Props> = ({ componente, marchi, onChiudi, onAggiornato, onEsito }) => {
  const [costruttore, setCostruttore] = useState('');
  const [modello, setModello] = useState('');
  const [codice, setCodice] = useState('');
  const [specs, setSpecs] = useState<Array<{ chiave: string; valore: string }>>([]);
  const [riconoscimento, setRiconoscimento] = useState<RiconoscimentoValori | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [caricando, setCaricando] = useState<MediaTipoAnagrafe | 'logo' | null>(null);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const uploadTarget = useRef<MediaTipoAnagrafe | 'logo'>('logo');

  useEffect(() => {
    if (!componente) return;
    setCostruttore(componente.costruttore ?? '');
    setModello(componente.modello ?? '');
    setCodice(componente.codice_costruttore ?? '');
    setSpecs(Object.entries(componente.specs).map(([chiave, v]) => ({ chiave, valore: testoDaValore(v) })));
    setRiconoscimento(valoriDaComponente(componente));
  }, [componente]);

  const marchioCorrente = useMemo(
    () => (riconoscimento?.marchio_id ? marchi.find((m) => m.id === riconoscimento.marchio_id) ?? null : null),
    [marchi, riconoscimento?.marchio_id],
  );

  const texture = useMemo(() => componente?.media?.find((m) => m.tipo === 'texture'), [componente]);
  const modello3d = useMemo(() => componente?.media?.find((m) => m.tipo === 'modello_3d'), [componente]);
  const documenti = useMemo(
    () => (componente?.media ?? []).filter((m) => m.tipo !== 'texture' && m.tipo !== 'modello_3d'),
    [componente],
  );

  if (!componente) return null;

  const salvaIdentita = async () => {
    setSalvando(true);
    try {
      const specsPulite: Record<string, unknown> = {};
      for (const r of specs) if (r.chiave.trim() !== '') specsPulite[r.chiave.trim()] = valoreDaTesto(r.valore);
      // Se c'è un marchio scelto, `costruttore` è la sua cache: lo allinea il servizio. Se non c'è,
      // vale quello battuto a mano (componenti davvero senza marchio o da assegnare).
      const patch = {
        ...(riconoscimento ? aPatch(riconoscimento) : {}),
        costruttore: costruttore.trim() || null,
        modello: modello.trim() || null,
        codice_costruttore: codice.trim() || null,
        specs: specsPulite,
      };
      const riletto = await salvaComponente(componente.id, patch, marchi);
      onAggiornato(riletto);
      onEsito('success', 'Scheda salvata');
    } catch (e) {
      console.error('[anagrafe] salvataggio fallito:', e);
      onEsito('error', e instanceof Error ? e.message : 'Salvataggio fallito');
    } finally {
      setSalvando(false);
    }
  };

  const apriUpload = (target: MediaTipoAnagrafe | 'logo') => {
    uploadTarget.current = target;
    inputFileRef.current?.click();
  };

  const fileScelto = async (file: File | undefined) => {
    if (!file || !componente) return;
    const target = uploadTarget.current;
    setCaricando(target);
    try {
      if (target === 'logo') {
        const voce = await caricaMediaSuStorage(componente.id, 'immagine', file);
        onAggiornato(await salvaComponente(componente.id, { logo_url: voce.download_url }));
      } else {
        const voce = await caricaMediaSuStorage(componente.id, target, file);
        const media = mediaAggiornati(componente.media, voce);
        onAggiornato(await salvaComponente(componente.id, { media }));
      }
      onEsito('success', 'File caricato e collegato al componente');
    } catch (e) {
      console.error('[anagrafe] upload fallito:', e);
      onEsito('error', e instanceof Error ? e.message : 'Caricamento fallito');
    } finally {
      setCaricando(null);
      if (inputFileRef.current) inputFileRef.current.value = '';
    }
  };

  const rimuoviMedia = async (voce: MediaAnagrafe) => {
    const media = (componente.media ?? []).filter((m) => m !== voce && m.download_url !== voce.download_url);
    try {
      onAggiornato(await salvaComponente(componente.id, { media }));
      onEsito('success', 'Media scollegato dal componente');
    } catch (e) {
      console.error('[anagrafe] rimozione media fallita:', e);
      onEsito('error', e instanceof Error ? e.message : 'Rimozione fallita');
    }
  };

  const accept =
    uploadTarget.current === 'modello_3d'
      ? '.glb,.gltf'
      : uploadTarget.current === 'texture' || uploadTarget.current === 'logo' || uploadTarget.current === 'immagine'
        ? 'image/*'
        : undefined;

  return (
    <Drawer anchor="right" open onClose={onChiudi} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
      <input
        ref={inputFileRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => fileScelto(e.target.files?.[0])}
      />

      <Box sx={{ p: 3, pb: 8 }}>
        {/* CARTA D'IDENTITÀ */}
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Avatar
            src={componente.logo_url}
            variant="rounded"
            sx={{ width: 56, height: 56, bgcolor: 'grey.100', color: 'grey.500', fontSize: 22 }}
          >
            {(componente.costruttore ?? '?').slice(0, 1)}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {componente.costruttore ?? 'costruttore da assegnare'}
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{componente.modello ?? componente.id}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
              <SoftChip label={componente.categoria_code || 'senza categoria'} tone="default" />
              {componente.codice_costruttore && (
                <SoftChip label={`cod. ${componente.codice_costruttore}`} tone="info" />
              )}
              <SoftChip label={`${componente.origine_prodotti.length} prodotti collegati`} tone="success" />
              {componente.marchio_da_assegnare && <SoftChip label="marchio da assegnare" tone="warning" />}
              {componente.stato_curatela === 'proposto' && (
                <SoftChip label={`proposto da ${componente.proposto_da_tenant ?? 'un tenant'}`} tone="gold" />
              )}
              {!componente.attivo && <SoftChip label={componente.fuso_in ? 'fuso in un altro' : 'spento'} tone="error" />}
            </Stack>
          </Box>
          <IconButton onClick={onChiudi} aria-label="Chiudi la scheda" sx={{ width: ALTEZZA_TOCCO, height: ALTEZZA_TOCCO }}><CloseIcon /></IconButton>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontFamily: 'var(--f-mono)' }}>
          {componente.id}
        </Typography>

        {componente.fuso_in && (
          <Alert severity="info" sx={{ mt: 1.5, py: 0.5 }}>
            Questo componente è stato fuso in <code>{componente.fuso_in}</code>: resta in archivio perché
            qualcosa potrebbe ancora citarlo, ma la verità tecnica vive là.
          </Alert>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* IDENTITÀ */}
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Identità</Typography>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="costruttore"
              size="small"
              fullWidth
              value={marchioCorrente ? marchioCorrente.nome_canonico : costruttore}
              onChange={(e) => setCostruttore(e.target.value)}
              InputProps={{ readOnly: !!marchioCorrente }}
              helperText={marchioCorrente ? 'segue il marchio scelto qui sotto' : undefined}
            />
            <TextField label="codice del costruttore" size="small" fullWidth value={codice} onChange={(e) => setCodice(e.target.value)} />
          </Stack>
          <TextField label="modello" size="small" fullWidth value={modello} onChange={(e) => setModello(e.target.value)} />
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {/* RICONOSCIMENTO IN MAGAZZINO (capitolo 162) */}
        {riconoscimento && (
          <RiconoscimentoMagazzino
            componente={componente}
            codiceCostruttoreCorrente={codice}
            marchi={marchi}
            valori={riconoscimento}
            onChange={setRiconoscimento}
          />
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* SPECIFICHE */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>Specifiche tecniche</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Sono la BASE dei parametri di ogni prodotto collegato (il prodotto può solo sovrascriverle).
          Stessi nomi dei parametri: <code>peso_kg</code>, <code>lunghezza_m</code>, …
        </Typography>
        <Stack spacing={1}>
          {specs.map((r, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="nome (es. peso_kg)"
                value={r.chiave}
                onChange={(e) => setSpecs((prev) => prev.map((x, j) => (j === i ? { ...x, chiave: e.target.value } : x)))}
                sx={{ flex: 1 }}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
              <TextField
                size="small"
                placeholder="valore"
                value={r.valore}
                onChange={(e) => setSpecs((prev) => prev.map((x, j) => (j === i ? { ...x, valore: e.target.value } : x)))}
                sx={{ flex: 1 }}
              />
              <IconButton size="small" aria-label="Rimuovi la specifica" onClick={() => setSpecs((prev) => prev.filter((_, j) => j !== i))}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            startIcon={<AddCircleOutline />}
            size="small"
            onClick={() => setSpecs((prev) => [...prev, { chiave: '', valore: '' }])}
            sx={{ alignSelf: 'flex-start' }}
          >
            Aggiungi specifica
          </Button>
        </Stack>

        <Button variant="contained" onClick={salvaIdentita} disabled={salvando} sx={{ mt: 2, minHeight: ALTEZZA_TOCCO }}>
          {salvando ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Salva scheda'}
        </Button>

        <Divider sx={{ my: 2.5 }} />

        {/* MEDIA UNIVERSALI */}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Media universali</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Valgono per TUTTI i tenant che vendono questo componente; un prodotto può
          sovrascriverli localmente coi propri media.
        </Typography>

        <Stack spacing={1.5}>
          {/* logo */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={componente.logo_url} variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}>🏷</Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">Logo del costruttore</Typography>
              <Typography variant="caption" color="text.secondary">
                {componente.logo_url ? 'presente — compare sulle sagome 3D e nelle schede' : 'assente'}
              </Typography>
            </Box>
            <Button size="small" startIcon={caricando === 'logo' ? <CircularProgress size={14} /> : <UploadFileOutlined />} disabled={caricando !== null} onClick={() => apriUpload('logo')}>
              {componente.logo_url ? 'Sostituisci' : 'Carica'}
            </Button>
          </Stack>

          {/* texture */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar src={texture?.download_url} variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}>🧱</Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">Texture del materiale</Typography>
              <Typography variant="caption" color="text.secondary">
                {texture ? texture.nome ?? 'presente — la resa 3D la stende sulle sagome' : 'assente'}
              </Typography>
            </Box>
            {texture && (
              <Tooltip title="Scollega la texture dal componente">
                <IconButton size="small" onClick={() => rimuoviMedia(texture)}><DeleteOutline fontSize="small" /></IconButton>
              </Tooltip>
            )}
            <Button size="small" startIcon={caricando === 'texture' ? <CircularProgress size={14} /> : <UploadFileOutlined />} disabled={caricando !== null} onClick={() => apriUpload('texture')}>
              {texture ? 'Sostituisci' : 'Carica'}
            </Button>
          </Stack>

          {/* modello 3D */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: modello3d ? 'primary.50' : 'grey.100', color: modello3d ? 'primary.main' : 'grey.500' }}>
              <ViewInAr fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2">Modello 3D (GLB)</Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {modello3d ? modello3d.nome ?? 'presente — usato dalla simulazione 3D' : 'assente: la simulazione usa la primitiva quotata'}
              </Typography>
            </Box>
            {modello3d && (
              <Tooltip title="Scollega il modello 3D dal componente">
                <IconButton size="small" onClick={() => rimuoviMedia(modello3d)}><DeleteOutline fontSize="small" /></IconButton>
              </Tooltip>
            )}
            <Button size="small" startIcon={caricando === 'modello_3d' ? <CircularProgress size={14} /> : <UploadFileOutlined />} disabled={caricando !== null} onClick={() => apriUpload('modello_3d')}>
              {modello3d ? 'Sostituisci' : 'Carica'}
            </Button>
          </Stack>

          {/* schede e documenti */}
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.100', color: 'grey.600' }}>
              <DescriptionOutlined fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2">Schede e documenti</Typography>
              {documenti.length === 0 && <Typography variant="caption" color="text.secondary">nessuno</Typography>}
              {documenti.map((m, i) => (
                <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" component="a" href={m.download_url} target="_blank" rel="noreferrer" sx={{ color: 'primary.main', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.nome ?? m.tipo}
                  </Typography>
                  <IconButton size="small" aria-label="Scollega il documento" onClick={() => rimuoviMedia(m)}>
                    <DeleteOutline sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>
              ))}
            </Box>
            <Button size="small" startIcon={caricando === 'scheda_tecnica' ? <CircularProgress size={14} /> : <UploadFileOutlined />} disabled={caricando !== null} onClick={() => apriUpload('scheda_tecnica')}>
              Aggiungi
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {/* PRODOTTI COLLEGATI */}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Prodotti collegati</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          I «vestiti commerciali» nei cataloghi dei tenant (fotografia della corsa dell'anagrafe).
        </Typography>
        {componente.origine_prodotti.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.25 }}>Nessun prodotto collegato risulta dalla corsa.</Alert>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {componente.origine_prodotti.map((idProdotto) => (
              <Typography key={idProdotto} component="li" variant="caption" sx={{ fontFamily: 'monospace' }}>
                {idProdotto}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default SchedaComponente;
